/**
 * Package request pipeline: approve, confirm payment, assign team, plan content.
 *
 * Replaces `fetchAdminPackageRequests`, `renderPackageRequestsList` and
 * `updatePackageRequestsBadge` — all of which lived in `package_requests.js`,
 * a file `index.html` still loads on line 2823 but which does not exist on disk.
 * Every one of those functions has been 404-ing in production; this screen is
 * the first working implementation of the flow.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Modal,
  Panel,
  SectionTitle,
  Select,
  Spinner,
  StatCard,
} from '../../components/common';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { adminApi, teamApi, topicApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { isoToDisplayDate } from '../../domain/dates';

const STATUS_META = {
  pending: { label: 'قيد المراجعة', className: 'border-amber-500/30 bg-amber-500/10 text-amber-300' },
  approved: { label: 'تمت الموافقة', className: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300' },
  confirmed: { label: 'مؤكدة', className: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300' },
  active: { label: 'جارية', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' },
  completed: { label: 'مكتملة', className: 'border-slate-600 bg-slate-800 text-slate-300' },
  rejected: { label: 'مرفوضة', className: 'border-red-500/30 bg-red-500/10 text-red-300' },
  expired: { label: 'منتهية', className: 'border-slate-600 bg-slate-800 text-slate-400' },
};

const STATUS_FLOW = ['pending', 'approved', 'active', 'completed'];

const TABS = [
  { id: 'open', label: 'المفتوحة', icon: 'inbox' },
  { id: 'active', label: 'الجارية', icon: 'play-circle' },
  { id: 'closed', label: 'المغلقة', icon: 'archive' },
];

export function AdminPackageRequests() {
  const { teamMembers } = useData();
  const { success, error: toastError } = useNotifications();

  const state = useAsync(() => adminApi.packageRequests(), [], { initialData: [] });
  const requests = useMemo(() => state.data || [], [state.data]);

  const [tab, setTab] = useState('open');
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const groups = useMemo(() => {
    const result = { open: [], active: [], closed: [] };
    for (const request of requests) {
      const status = request.status || 'pending';
      if (['pending', 'approved', 'confirmed'].includes(status)) result.open.push(request);
      else if (status === 'active') result.active.push(request);
      else result.closed.push(request);
    }
    return result;
  }, [requests]);

  const totals = useMemo(
    () => ({
      total: requests.length,
      pending: requests.filter((r) => (r.status || 'pending') === 'pending').length,
      unpaid: requests.filter((r) => !r.paymentConfirmed).length,
      revenue: requests
        .filter((r) => r.paymentConfirmed)
        .reduce((sum, r) => sum + (Number(r.paidAmount || r.amount_paid || r.price) || 0), 0),
    }),
    [requests],
  );

  const tabsWithCounts = TABS.map((t) => ({ ...t, count: groups[t.id].length }));

  const setStatus = async (request, status) => {
    setBusy(true);
    try {
      await adminApi.setRequestStatus(request.id, { status });
      success(`تم تغيير الحالة إلى "${STATUS_META[status]?.label || status}"`);
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر تغيير الحالة');
    } finally {
      setBusy(false);
    }
  };

  const setPayment = async (request, paymentConfirmed) => {
    setBusy(true);
    try {
      await adminApi.setRequestStatus(request.id, { paymentConfirmed });
      success(paymentConfirmed ? 'تم تأكيد الدفع' : 'تم إلغاء تأكيد الدفع');
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر تحديث حالة الدفع');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* The legacy header was a panel with an orange spine, not a plain page
          title — the colour is how the section is recognised from the sidebar,
          which is orange there too. */}
      <Panel className="border-s-4 border-s-orange-500">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-xl font-bold text-white">
              <Icon name="shopping-cart" size={24} className="text-orange-400" />
              طلبات الباقات
            </h3>
            <p className="mt-1 text-xs text-slate-400">{requests.length} طلب</p>
          </div>

          <Button variant="ghost" icon="refresh-cw" onClick={state.reload} loading={state.loading}>
            تحديث
          </Button>
        </div>

        {/* Filter pills, as the legacy had them, rather than underline tabs. */}
        <div className="mt-5 flex flex-wrap gap-2">
          {tabsWithCounts.map((item) => {
            const active = item.id === tab;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                aria-pressed={active}
                className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold transition-all ${
                  active
                    ? 'border-orange-500/50 bg-orange-500/15 text-orange-300'
                    : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/25 hover:text-slate-200'
                }`}
              >
                <Icon name={item.icon} size={15} />
                {item.label}
                <span
                  className={`rounded-full px-1.5 text-[10px] ${
                    active ? 'bg-orange-500/25' : 'bg-white/10'
                  }`}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>
      </Panel>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="inbox" label="إجمالي الطلبات" value={totals.total} accent="cyan" />
        <StatCard icon="clock" label="بانتظار المراجعة" value={totals.pending} accent="amber" />
        <StatCard icon="credit-card" label="غير مدفوعة" value={totals.unpaid} accent="red" />
        <StatCard
          icon="banknote"
          label="إيرادات محصّلة"
          value={formatCurrency(totals.revenue)}
          accent="emerald"
        />
      </div>

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && requests.length === 0 ? (
        <Spinner label="جاري تحميل الطلبات..." />
      ) : groups[tab].length === 0 ? (
        <Panel>
          <EmptyState icon="inbox" title="لا توجد طلبات في هذا التبويب" />
        </Panel>
      ) : (
        <div className="space-y-3">
          {groups[tab].map((request) => (
            <RequestRow
              key={request.id}
              request={request}
              busy={busy}
              onOpen={() => setDetailsTarget(request)}
              onSetStatus={(status) => setStatus(request, status)}
              onSetPayment={(paid) => setPayment(request, paid)}
            />
          ))}
        </div>
      )}

      <RequestDetailsModal
        request={detailsTarget}
        teamMembers={teamMembers}
        onClose={() => setDetailsTarget(null)}
        onChanged={() => {
          state.reload();
          success('تم حفظ التغييرات');
        }}
        onError={toastError}
      />
    </div>
  );
}

/* ========================================================================== */

function RequestRow({ request, busy, onOpen, onSetStatus, onSetPayment }) {
  const status = request.status || 'pending';
  const meta = STATUS_META[status] || STATUS_META.pending;
  const pkg = request.Package || {};
  const doctor = request.User || {};
  const assigned = request.AssignedTeamMembers || [];

  const nextStatus = STATUS_FLOW[STATUS_FLOW.indexOf(status) + 1];

  return (
    <Panel className="flex flex-wrap items-center gap-4">
      <span className="rounded-xl bg-purple-500/10 p-3 text-purple-400">
        <Icon name="package" size={20} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate font-bold text-white">{pkg.name || `طلب #${request.id}`}</h3>
          <Badge className={meta.className}>{meta.label}</Badge>
          <Badge
            className={
              request.paymentConfirmed
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                : 'border-red-500/30 bg-red-500/10 text-red-300'
            }
            icon={request.paymentConfirmed ? 'check' : 'credit-card'}
          >
            {request.paymentConfirmed ? 'مدفوعة' : 'غير مدفوعة'}
          </Badge>
        </div>

        <p className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <Icon name="stethoscope" size={12} />
            {doctor.name || `طبيب #${request.userId}`}
          </span>
          <span className="font-bold text-emerald-400">
            {formatCurrency(request.amount_paid || request.price)}
          </span>
          {request.requestDate && (
            <span>{isoToDisplayDate(String(request.requestDate).slice(0, 10))}</span>
          )}
          {assigned.length > 0 && (
            <span className="flex items-center gap-1">
              <Icon name="users" size={12} />
              {assigned.map((m) => m.name).join('، ')}
            </span>
          )}
        </p>
      </div>

      {/* Not `shrink-0`: a flex item that may not shrink keeps its max-content
          width, and these three buttons are ~355px of it — more than a 393px
          phone leaves inside the panel. Its own `flex-wrap` could not rescue it,
          because wrapping only begins once something narrows the box. No
          `min-w-0` either, unlike the text column beside it: this row should
          stay as wide as its widest button, and that always fits. */}
      <div className="flex flex-wrap gap-2">
        {!request.paymentConfirmed && (
          <Button variant="success" icon="credit-card" loading={busy} onClick={() => onSetPayment(true)}>
            تأكيد الدفع
          </Button>
        )}
        {nextStatus && (
          <Button variant="ghost" icon="arrow-left" loading={busy} onClick={() => onSetStatus(nextStatus)}>
            {STATUS_META[nextStatus].label}
          </Button>
        )}
        <Button variant="subtle" icon="settings-2" onClick={onOpen}>
          إدارة
        </Button>
      </div>
    </Panel>
  );
}

/* ========================================================================== */

function RequestDetailsModal({ request, teamMembers, onClose, onChanged, onError }) {
  const [assigneeId, setAssigneeId] = useState('');
  const [busy, setBusy] = useState(false);

  const topicsState = useAsync(
    () => topicApi.listForPackage(request.id),
    [request?.id],
    { initialData: [], enabled: Boolean(request?.id) },
  );

  useEffect(() => {
    setAssigneeId('');
  }, [request?.id]);

  if (!request) return null;

  const pkg = request.Package || {};
  const doctor = request.User || {};
  const assigned = request.AssignedTeamMembers || [];

  const assign = async () => {
    if (!assigneeId) return;
    setBusy(true);
    try {
      await teamApi.assignProject({ teamMemberId: Number(assigneeId), packageRequestId: request.id });
      onChanged();
      setAssigneeId('');
    } catch (err) {
      onError(err.message || 'تعذر إسناد المشروع');
    } finally {
      setBusy(false);
    }
  };

  const assignTopic = async (topicId, memberId) => {
    setBusy(true);
    try {
      await topicApi.update(topicId, { assigned_team_member_id: memberId ? Number(memberId) : null });
      topicsState.reload();
    } catch (err) {
      onError(err.message || 'تعذر إسناد الموضوع');
    } finally {
      setBusy(false);
    }
  };

  const approveTopic = async (topicId, isApproved) => {
    setBusy(true);
    try {
      await topicApi.setStatus(topicId, { is_approved: isApproved });
      topicsState.reload();
    } catch (err) {
      onError(err.message || 'تعذر تحديث الموضوع');
    } finally {
      setBusy(false);
    }
  };

  const available = teamMembers.filter((m) => !assigned.some((a) => a.id === m.id));

  return (
    <Modal open onClose={onClose} title="إدارة الطلب" icon="settings-2" size="lg">
      <div className="space-y-6">
        {/* Summary */}
        <div className="rounded-xl border border-white/5 bg-white/5 p-3">
          <h3 className="font-bold text-white">{pkg.name}</h3>
          <p className="mt-0.5 text-xs text-slate-400">
            {doctor.name} — {formatCurrency(request.amount_paid || request.price)}
          </p>
        </div>

        {/* Team assignment */}
        <section>
          <SectionTitle icon="users">فريق العمل</SectionTitle>

          {assigned.length > 0 ? (
            <div className="mb-3 flex flex-wrap gap-2">
              {assigned.map((member) => (
                <Badge key={member.id} className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="user">
                  {member.name}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="mb-3 text-xs text-slate-500">لم يتم إسناد الطلب لأي عضو بعد.</p>
          )}

          <div className="flex gap-2">
            <Select
              value={assigneeId}
              onChange={(event) => setAssigneeId(event.target.value)}
              className="flex-1"
            >
              <option value="">اختر عضواً لإسناده</option>
              {available.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                  {member.capacityStatus === 'full' ? ' (مشغول)' : ''}
                </option>
              ))}
            </Select>
            <Button icon="user-plus" loading={busy} disabled={!assigneeId} onClick={assign}>
              إسناد
            </Button>
          </div>
        </section>

        {/* Content topics */}
        <section className="border-t border-white/10 pt-5">
          <SectionTitle icon="list-checks">مواضيع المحتوى</SectionTitle>

          {topicsState.loading ? (
            <Spinner variant="inline" label="جاري تحميل المواضيع..." />
          ) : (topicsState.data || []).length === 0 ? (
            <EmptyState
              icon="file-question"
              title="لا توجد مواضيع بعد"
              message="سيضيف الطبيب مواضيع المحتوى من صفحة باقاته."
            />
          ) : (
            <ul className="space-y-2">
              {(topicsState.data || []).map((topic) => (
                <li key={topic.id} className="rounded-xl border border-white/5 bg-white/5 p-3">
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-white">
                        {topic.topic_title || 'بدون عنوان'}
                      </p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
                        {topic.post_date && (
                          <span>{isoToDisplayDate(String(topic.post_date).slice(0, 10))}</span>
                        )}
                        <span>بواسطة {topic.created_by}</span>
                        {topic.AssignedMember && <span>— {topic.AssignedMember.name}</span>}
                      </p>
                    </div>

                    <Badge
                      className={
                        topic.is_approved
                          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                          : 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                      }
                    >
                      {topic.is_approved ? 'معتمد' : 'بانتظار الاعتماد'}
                    </Badge>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <Select
                      value={topic.assigned_team_member_id ?? ''}
                      onChange={(event) => assignTopic(topic.id, event.target.value)}
                      className="!py-1.5 flex-1 text-xs"
                    >
                      <option value="">غير مسند</option>
                      {teamMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </Select>

                    <Button
                      variant={topic.is_approved ? 'subtle' : 'success'}
                      icon={topic.is_approved ? 'rotate-ccw' : 'check'}
                      loading={busy}
                      onClick={() => approveTopic(topic.id, !topic.is_approved)}
                    >
                      {topic.is_approved ? 'إلغاء الاعتماد' : 'اعتماد'}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
}

export default AdminPackageRequests;
